import { LoaderFunctionArgs, useLoaderData, useNavigate } from 'react-router'
import { useIntercom } from 'react-use-intercom'
import Footer from '~/components/Footer'
import Navigation from '~/components/Navigation'
import { Button } from '~/components/ui/button'
import { getPricesForCountry } from '~/features/premium/getPricesForCountry'
import { proPlans } from '~/features/premium/proPlans'
import useTranslator from '~/lib/useTranslator'
import { getLocation } from '~/utils/getLocation'
import { mergeMeta } from '~/utils/mergeMeta'

export const meta = mergeMeta(() => {
  return [
    { title: 'Heja | Pricing' },
    {
      name: 'description',
      content: 'Pricing for paid plans',
    },
  ]
})

export async function loader({ request }: LoaderFunctionArgs) {
  const ip = request.headers.get('cf-connecting-ip')
  const country = ip && (await getLocation(ip))?.country_code

  const prices = getPricesForCountry(country ?? '')
  return { country, prices }
}

export default function Pricing() {
  const { country, prices } = useLoaderData<typeof loader>()
  const Translator = useTranslator()
  const navigate = useNavigate()
  const { show } = useIntercom()

  return (
    <>
      <Navigation color="purple" />
      <header className="bg-secondary-color text-white py-12 px-4 text-center w-full">
        <div className="flex flex-col gap-4 w-full max-w-3xl mx-auto">
          <h1 className="text-white mb-1 font-bold">
            Team Pro <span>{Translator.singular('Pricing')}</span>
          </h1>
          <p className="text-white/80 text-xl leading-normal">
            {Translator.singular(
              'Heja’s paid plans give you the tools you need to be the leader you want to be. Easier planning, more control and smarter communication — so you can save your sweat for training, and not for your schedule.'
            )}
          </p>
        </div>
      </header>
      <main className="w-full px-4 bg-linear-to-b from-secondary-color from-5% to-transparent md:from-[6.25rem] md:to-[6.25rem]">
        <div className="w-full shadow-lg max-w-7xl mx-auto flex flex-col gap-6 md:gap-1 md:bg-gray-50 md:bg-clip-padding md:flex-row md:rounded-2xl md:overflow-hidden md:border-4 md:border-white/50">
          {proPlans(country ?? 'en')
            .slice(0, 2)
            .map((plan, idx) => (
              <section
                key={idx}
                className="bg-white rounded-lg py-4 text-black/80 shadow-xl md:shadow-none md:rounded-none"
              >
                <div
                  className="px-4 pb-4 border-b-4 flex items-center justify-between"
                  style={{ borderColor: plan.color }}
                >
                  <div>
                    <h2 className="text-3xl font-bold" style={{ color: plan.color }}>
                      {plan.title}
                    </h2>
                    <p className="text-base font-normal text-slate-600">
                      {plan.id === 'free' && Translator.singular('No cost — Ad supported')}
                      {plan.id !== 'free' &&
                        Translator.singular('From {{ price }}', {
                          price: prices[plan.id as 'pro'],
                        })}
                    </p>
                  </div>

                  {plan.id === 'pro' && (
                    <Button
                      className="hidden md:block bg-black font-normal hover:bg-black/95 cursor-pointer"
                      size="lg"
                      onClick={() => navigate('/buy-pro/upgrade')}
                    >
                      {Translator.singular('Upgrade now')}
                    </Button>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-normal">{plan.header}</h3>
                </div>
                <dl
                  className={`px-4 grid mb-6 ${plan.id === 'free' ? 'grid-cols-1' : 'sm:grid-cols-2'} gap-x-4 gap-y-3`}
                >
                  {plan.features.map((feat, idx) => (
                    <div key={idx}>
                      <p className="font-medium text-lg">{feat.title}</p>
                      <p className="text-sm leading-snug text-slate-600">{feat.desc}</p>
                    </div>
                  ))}
                </dl>
                {plan.id === 'pro' && (
                  <div className="flex px-4 md:hidden">
                    <button
                      className="bg-secondary-color flex-1 text-white px-4 py-2 rounded-md"
                      onClick={() => navigate('/buy-pro/upgrade')}
                    >
                      {Translator.singular('Upgrade to Team Pro')}
                    </button>
                  </div>
                )}
                {plan.id === 'pro' && (
                  <div className="max-md:border-t mt-4 px-4">
                    <div className="bg-[#2E2078] mt-4 md:mt-10 md:-mx-2 rounded-lg px-4 pt-4 pb-4 pr-6 flex text-white items-center justify-between flex-col lg:flex-row gap-4">
                      <div className="max-md:text-center">
                        <p className="text-base font-semibold text-[#D5D3F8]">Team Pro Max</p>
                        <p className="text-2xl mt-2 font-semibold text-white">
                          Ad free for all members
                        </p>
                        <p className="text-sm opacity-60">
                          Includes all Team Pro features + Ad free for all members in the team.
                        </p>
                      </div>
                      <button
                        className="rounded-md text-base font-normal px-6 leading-tight py-2.5 bg-[#7f6dd7]/80 hover:bg-[#7f6dd7]/90"
                        onClick={() => navigate('/buy-pro/upgrade')}
                      >
                        <span className="md:hidden">
                          {Translator.singular('Upgrade to Team Pro Max')}
                        </span>
                        <span className="hidden md:block">
                          {Translator.singular('Upgrade now')}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </section>
            ))}
        </div>
      </main>

      <section className="mx-4">
        <aside className="mt-8 max-w-7xl md:mx-auto rounded-2xl bg-clip-padding relative p-[0.15rem] bg-linear-to-tr from-primary-color via-orange-500 to-secondary-color">
          <div className="bg-white rounded-[14px] py-6 px-5 text-center flex flex-col gap-4 md:flex-row md:text-left justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">
                {Translator.singular('Interested in a Club deal?')}
              </h2>
              <p className="text-base max-w-lg">
                {Translator.singular(
                  'If you’re considering a paid plan for several of your club’s teams, you can talk to our sales, and we can work something out.'
                )}
              </p>
            </div>

            <button
              type="button"
              className="bg-premium-color text-white px-16 font-normal md:font-semibold md:text-lg py-2 rounded-md shadow-lg"
              onClick={(e) => {
                e.preventDefault()
                show()
              }}
            >
              {Translator.singular('Chat with us')}
            </button>
          </div>
        </aside>
      </section>

      <Footer />
    </>
  )
}
